import * as React from 'react';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { Label } from '@independent-software/typeui/controls/Label';

import { AuthContext } from '../../AuthContext';
import { Sample, Campaign, CampaignFactory, Station, StationFactory } from '../../resource/';
import { Query } from '../../services/';
import { Section } from '../../modules';
import { TidalSituation } from '../Campaign/TidalSituation';

interface IProps {
  /** Initial form data. */
  data: Sample;
  /** Called whenever form changes. */
  onChange: (data: Sample, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

const SampleForm = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const [campaigns, setCampaigns] = React.useState([] as Campaign[]);
  const [stations, setStations] = React.useState([] as Station[]);

  React.useEffect(() => {
    handleCampaignSearch();
    handleStationSearch();
  }, []);

  const handleCampaignSearch = (q?: string) => {
    // Retrieve a list of campaigns:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    CampaignFactory.getSome(auth, 0, 16, query)
      .then((res) => setCampaigns(res.items));
  }

  const handleStationSearch = (q?: string) => {
    // Retrieve a list of stations:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    StationFactory.getSome(auth, 0, 16, query)
      .then((res) => setStations(res.items));
  }

  const renderCampaign = () => 
    <Form.Field
      required={{message: "Campaign is required."}}
      width={1} label="Campaign"
      name="campaign" 
      value={props.data.campaign}
      control={(<Dropdown onSearch={handleCampaignSearch} fluid data={campaigns} placeholder="Campaign" label={(item:Campaign) => item.name}>
        <Dropdown.Column weight={1}>{(item:Campaign) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a campaign."/>    

  const renderStation = () => 
    <Form.Field
      required={{message: "Station is required."}}
      width={1} label="Station"
      name="station" 
      value={props.data.station}
      control={(<Dropdown onSearch={handleStationSearch} fluid data={stations} placeholder="Station" label={(item:Station) => item.name}>
        <Dropdown.Column weight={1}>{(item:Station) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a station."/>   

  const renderDate = () =>
    <Form.Field
      name="date" label="Date"
      required={{message: "Sample data is required."}}
      value={props.data.date}
      control={(<Input type="date" nofuture placeholder="Date" fluid/>)}
      hint={(<React.Fragment>Please select a date for the sample.</React.Fragment>)}/>  

  const renderTime = () =>
    <Form.Field
      name="time" label="Time"
      value={props.data.time}
      control={(<Input type="time" is24h hasSeconds clock placeholder="Time" fluid/>)}
      hint={(<React.Fragment>Please select a time for the sample.</React.Fragment>)}/>        

  const renderTidalSituation = () =>
    <Form.Field 
      name="tidal_situation" label="Tidal situtation"
      value={props.data.tidal_situation}
      control={<Dropdown placeholder="Tidal situation" fluid data={TidalSituation.getList()}
        label={(item) => TidalSituation.toName(item)}>
        <Dropdown.Column>{(item) => TidalSituation.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select the tide condition.</React.Fragment>)}/>         
          
  const renderDepthType = () =>      
    <Form.Field
      name="depth_type" label="Depth level"
      value={props.data.depth_type}
      control={(<Input type="text" placeholder="Depth level" fluid/>)}
      hint={(<React.Fragment>Please enter a sampling position (e.g. surface, mid-water, bottom).</React.Fragment>)}/>  

  const renderDepth = () =>      
    <Form.Field
      name="depth" label="Depth"
      value={props.data.depth}
      control={(<Input type="text" placeholder="Depth" fluid><Label pointing="left" attached="right">m</Label></Input>)}
      required={{message: "Sample depth is required."}}
      isFloat={{message: 'Depth must be numeric.'}}
      min={{value: 0, message: 'Must be a positive number.'}}
      hint={(<React.Fragment>Please enter a depth from the surface at which sample is collected.</React.Fragment>)}/>  

  const renderInterval = () =>      
    <Form.Field
      name="interval" label="Interval"
      value={props.data.interval}
      control={(<Input type="text" placeholder="Interval" fluid><Label pointing="left" attached="right">min</Label></Input>)}
      isFloat={{message: 'Interval must be numeric.'}}
      min={{value: 0, message: 'Must be a positive number.'}}
      hint={(<React.Fragment>Please enter a the time taken to collect the sample.</React.Fragment>)}/>    

  return (
    <Form
      data={props.data} dirty={props.dirty} onChange={props.onChange} onValidate={props.onValidate}>
      <Tabs underlined>
        <Tabs.Pane label="General">
          <Section padded>
            <Flex.Columns count={2}>
              {renderCampaign()}
              {renderStation()}
              {renderDate()}
              {renderDepth()}
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
        <Tabs.Pane label="Details">
          <Section padded>
            <Flex.Columns count={2}>
              {renderTime()}
              {renderInterval()}
              {renderTidalSituation()}
              {renderDepthType()}
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
      </Tabs>
    </Form>
  )
}

export { SampleForm }
